/**
 * V4RunDetailPanel — Phase 7a-3.
 *
 * Floating slideout (top-right of the canvas surface) that shows the
 * detail of a single workflow run. Three tabs:
 *
 *   • Timeline  — per-node execution timeline with status + duration.
 *                  Click a row to jump to that node on the canvas.
 *   • Output    — final result JSON, pretty-printed and copy-able.
 *   • Logs      — chronological events stream.
 *
 * Data sources:
 *   - api.getWorkflowRun(runId)      → run summary (status, timing, output)
 *   - api.getNodeExecutions(runId)   → per-node execution rows
 *
 * Polls while the run is in a non-terminal state (RUNNING / PAUSED /
 * PENDING_APPROVAL) so the timeline updates without a manual refresh.
 * Polling stops on terminal status to avoid hammering the BE for done
 * runs the operator left open.
 *
 * Positioning mirrors V4ValidationPanel: position:absolute, top:12 /
 * right:12, width:420, max-height fills the canvas. Higher z-index so
 * it floats above the validation panel when both are open.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Clock, FileJson, ScrollText, RefreshCw,
  ArrowDownToLine, ShieldCheck,
} from 'lucide-react';
import * as api from '../../services/api';

const TABS = [
  { key: 'timeline', label: 'Timeline', icon: Clock },
  { key: 'input',    label: 'Input',    icon: ArrowDownToLine },
  { key: 'output',   label: 'Output',   icon: FileJson },
  { key: 'logs',     label: 'Logs',     icon: ScrollText },
  { key: 'audit',    label: 'Audit',    icon: ShieldCheck },
];

const STATUS_TONE = {
  COMPLETED:        { bg: '#d1fae5', fg: '#065f46', label: 'Completed' },
  COMPLETED_WITH_ERRORS: { bg: '#fef3c7', fg: '#92400e', label: 'Partial' },
  RUNNING:          { bg: '#dbeafe', fg: '#1e40af', label: 'Running' },
  PAUSED:           { bg: '#e5e7eb', fg: '#475569', label: 'Paused' },
  PENDING_APPROVAL: { bg: '#ede9fe', fg: '#5b21b6', label: 'Awaiting approval' },
  FAILED:           { bg: '#fee2e2', fg: '#991b1b', label: 'Failed' },
  TERMINATED:       { bg: '#fee2e2', fg: '#991b1b', label: 'Terminated' },
  TIMED_OUT:        { bg: '#fef2f2', fg: '#7f1d1d', label: 'Timed out' },
  PENDING:          { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  SKIPPED:          { bg: '#f1f5f9', fg: '#94a3b8', label: 'Skipped' },
};

const TERMINAL_STATUSES = new Set([
  'COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'TERMINATED', 'TIMED_OUT',
]);

// Auto-poll every 2s while non-terminal. Stops on terminal.
const POLL_MS = 2000;

export default function V4RunDetailPanel({ runId, onClose, onJumpToNode, embedded = false, selectedCanvasNodeId = null }) {
  const [run, setRun] = useState(null);          /* run summary */
  const [nodes, setNodes] = useState([]);        /* per-node executions */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('timeline');
  // Phase 7q-4 — audit log fetched lazily when the operator opens the
  // tab. Keeps the dock cheap on first mount.
  const [auditEntries, setAuditEntries] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const pollTimerRef = useRef(null);

  // Fetcher. Calls both summary + nodes in parallel. Tolerant of
  // BE envelope shapes.
  const fetchAll = useMemo(() => async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const [runRes, nodesRes] = await Promise.all([
        api.getWorkflowRun(runId),
        api.getNodeExecutions(runId),
      ]);
      const runPayload = (runRes && 'data' in runRes) ? runRes.data : runRes;
      const nodesPayload = (nodesRes && 'data' in nodesRes) ? nodesRes.data : nodesRes;
      setRun(runPayload || null);
      const list = Array.isArray(nodesPayload)
        ? nodesPayload
        : (Array.isArray(nodesPayload?.items) ? nodesPayload.items : []);
      setNodes(list);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  // Initial fetch on runId change. Also resets the audit-tab cache so
  // a stale log doesn't appear if the operator switches runs.
  useEffect(() => {
    if (!runId) return undefined;
    fetchAll();
    setAuditEntries(null);
    setAuditError(null);
    return () => {};
  }, [runId, fetchAll]);

  // Phase 7q-4 — lazy fetch the audit log when the operator opens the
  // Audit tab. Cached for the duration of this run id.
  useEffect(() => {
    if (tab !== 'audit') return undefined;
    if (!runId) return undefined;
    if (auditEntries !== null || auditLoading) return undefined;
    let cancelled = false;
    setAuditLoading(true);
    setAuditError(null);
    api.getRunAuditLog(runId)
      .then((res) => {
        if (cancelled) return;
        const payload = (res && 'data' in res) ? res.data : res;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.entries) ? payload.entries
            : (Array.isArray(payload?.items) ? payload.items : []));
        setAuditEntries(list);
      })
      .catch((e) => { if (!cancelled) setAuditError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [tab, runId, auditEntries, auditLoading]);

  // Auto-poll while non-terminal. Re-arms on each successful fetch
  // because the status can flip terminal between calls.
  useEffect(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (!runId) return undefined;
    const status = String(run?.status || '').toUpperCase();
    if (TERMINAL_STATUSES.has(status)) return undefined;
    pollTimerRef.current = setTimeout(() => { fetchAll(); }, POLL_MS);
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [run, runId, fetchAll]);

  if (!runId) return null;

  const status = String(run?.status || '').toUpperCase();
  const tone = STATUS_TONE[status] || { bg: '#f1f5f9', fg: '#475569', label: status || 'Loading…' };
  const startedAt = run?.startedAt || run?.createdAt;
  const completedAt = run?.completedAt;
  const durationLabel = fmtDuration(startedAt, completedAt);

  return (
    <aside
      role="region"
      aria-label="Workflow run detail"
      style={embedded ? {
        width: '100%', height: '100%',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        fontFamily: 'inherit',
        overflow: 'hidden',
      } : {
        position: 'absolute',
        top: 12,
        right: 12,
        width: 420,
        maxHeight: 'calc(100% - 24px)',
        display: 'flex', flexDirection: 'column',
        background: '#ffffff',
        border: '0.5px solid #e2e8f0',
        borderRadius: 8,
        boxShadow: '0 14px 36px -10px rgba(15, 23, 42, 0.22), 0 3px 8px -2px rgba(15, 23, 42, 0.10)',
        zIndex: 9, // floats above the validation panel (z=8)
        fontFamily: 'inherit',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        borderBottom: '0.5px solid #e2e8f0',
      }}>
        <span style={{
          fontSize: 11.5, fontWeight: 700, color: '#0f172a',
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          Run
        </span>
        <code style={{
          fontSize: 10.5, color: '#64748b',
          fontFamily: 'SFMono-Regular, Menlo, monospace',
        }}>
          {String(runId).slice(0, 8)}
        </code>
        <span style={{
          padding: '2px 7px', borderRadius: 999,
          fontSize: 10, fontWeight: 700,
          color: tone.fg, background: tone.bg,
          textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
          {tone.label}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh run"
          style={iconBtnStyle}
        >
          <RefreshCw size={11} aria-hidden="true" style={{
            opacity: loading ? 0.5 : 1,
          }} />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close run detail"
          style={iconBtnStyle}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>

      {/* Meta line */}
      {(startedAt || durationLabel) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '6px 12px',
          fontSize: 10.5, color: '#64748b',
          borderBottom: '0.5px solid #e2e8f0',
        }}>
          {startedAt && (
            <span>Started {new Date(startedAt).toLocaleString()}</span>
          )}
          {durationLabel && (
            <span>· {durationLabel}</span>
          )}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={{
        display: 'flex', alignItems: 'center', gap: 0,
        padding: '0 8px',
        borderBottom: '0.5px solid #e2e8f0',
      }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setTab(t.key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '8px 10px',
                fontSize: 11, fontWeight: 600,
                color: active ? '#0f172a' : '#64748b',
                background: 'transparent',
                border: 'none',
                borderBottom: active ? '2px solid #0f172a' : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <Icon size={11} aria-hidden="true" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {error && (
          <div style={{ padding: 16, fontSize: 12, color: '#991b1b' }}>
            {error}
          </div>
        )}
        {!error && tab === 'timeline' && (
          <TimelineTab
            nodes={nodes}
            onJumpToNode={onJumpToNode}
            loading={loading && nodes.length === 0}
            selectedCanvasNodeId={selectedCanvasNodeId}
          />
        )}
        {!error && tab === 'input' && (
          <InputTab
            run={run}
            nodes={nodes}
            loading={loading && !run}
            selectedCanvasNodeId={selectedCanvasNodeId}
          />
        )}
        {!error && tab === 'output' && (
          <OutputTab
            run={run}
            nodes={nodes}
            loading={loading && !run}
            selectedCanvasNodeId={selectedCanvasNodeId}
          />
        )}
        {!error && tab === 'logs' && (
          <LogsTab nodes={nodes} loading={loading && nodes.length === 0} />
        )}
        {!error && tab === 'audit' && (
          <AuditTab
            entries={auditEntries}
            loading={auditLoading}
            error={auditError}
          />
        )}
      </div>
    </aside>
  );
}

/* ───── Timeline tab ──────────────────────────────────────────────── */
function TimelineTab({ nodes, onJumpToNode, loading, selectedCanvasNodeId }) {
  if (loading) return <Placeholder>Loading timeline…</Placeholder>;
  if (!nodes || nodes.length === 0) return <Placeholder>No node executions yet.</Placeholder>;
  // Sort by start time ascending — natural reading order, top→bottom
  // matches execution time arrow.
  const sorted = nodes.slice().sort((a, b) => {
    const ta = new Date(a.startedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.startedAt || b.createdAt || 0).getTime();
    return ta - tb;
  });
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {sorted.map((n, i) => {
        const status = String(n.status || '').toUpperCase();
        const tone = STATUS_TONE[status] || { bg: '#f1f5f9', fg: '#475569', label: status || '—' };
        const dur = fmtDuration(n.startedAt, n.completedAt);
        const sourceId = n.nodeId || n.canvasNodeId || n.sourceNodeId;
        const canJump = !!sourceId && typeof onJumpToNode === 'function';
        const RowEl = canJump ? 'button' : 'div';
        const isSelected = selectedCanvasNodeId && sourceId === selectedCanvasNodeId;
        return (
          <li key={n.id || `${sourceId}_${i}`} style={{ borderBottom: '0.5px solid #f1f5f9' }}>
            <RowEl
              type={canJump ? 'button' : undefined}
              onClick={canJump ? () => onJumpToNode(sourceId) : undefined}
              style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                width: '100%', padding: '8px 12px',
                background: isSelected ? '#ecfeff' : '#ffffff',
                border: 'none',
                /* must come AFTER `border: 'none'` so it wins the
                 * CSS shorthand precedence battle. */
                borderLeft: isSelected ? '3px solid #0891b2' : '3px solid transparent',
                textAlign: 'left',
                cursor: canJump ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  padding: '1px 6px', borderRadius: 999,
                  fontSize: 9, fontWeight: 700,
                  color: tone.fg, background: tone.bg,
                  textTransform: 'uppercase', letterSpacing: 0.3,
                }}>{tone.label}</span>
                <span style={{
                  fontSize: 12, color: '#0f172a',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1,
                }}>
                  {n.label || n.nodeLabel || sourceId || 'Node'}
                </span>
                {dur && <span style={{ fontSize: 10, color: '#94a3b8' }}>{dur}</span>}
              </div>
              {n.error && (
                <span style={{ fontSize: 10.5, color: '#991b1b' }}>
                  {String(n.error).slice(0, 200)}
                </span>
              )}
              {canJump && (
                <span style={{ fontSize: 10, color: '#0ea5e9' }}>Jump to node ↗</span>
              )}
            </RowEl>
          </li>
        );
      })}
    </ul>
  );
}

/* ───── Input tab — Phase 7q-4 ───────────────────────────────────── *
 * Surfaces (a) the workflow-level input the operator passed at Run
 * time (or that the trigger fired with), and (b) the per-node input
 * payload for the selected canvas node so the operator can debug a
 * specific node's reception of upstream data. When no node is
 * selected, the workflow-level input alone is shown.
 */
function InputTab({ run, nodes, loading, selectedCanvasNodeId }) {
  if (loading) return <Placeholder>Loading input…</Placeholder>;
  const workflowInput = run?.input ?? run?.inputs ?? null;
  const selectedNode = selectedCanvasNodeId
    ? (nodes || []).find((n) => {
        const id = n.nodeId || n.canvasNodeId || n.sourceNodeId;
        return id === selectedCanvasNodeId;
      })
    : null;
  const nodeInput = selectedNode?.input ?? selectedNode?.inputs ?? null;
  return (
    <div style={{ padding: 0 }}>
      <SectionHeader label="Workflow input" />
      <PayloadBlock
        payload={workflowInput}
        emptyText="No workflow input — the run executed without operator-provided parameters."
      />
      {selectedNode && (
        <>
          <SectionHeader
            label={`Selected node · ${selectedNode.label || selectedNode.nodeLabel || selectedCanvasNodeId}`}
          />
          <PayloadBlock
            payload={nodeInput}
            emptyText="This node hasn't received input yet (or the BE didn't surface it)."
          />
        </>
      )}
      {!selectedNode && (
        <div style={{
          padding: '10px 12px', fontSize: 10.5, color: '#94a3b8',
          borderTop: '0.5px solid #f1f5f9',
        }}>
          Tip: click a node on the canvas to see its per-task input.
        </div>
      )}
    </div>
  );
}

/* ───── Output tab — Phase 7q-5 enhanced for per-node debug ─────── */
function OutputTab({ run, nodes, loading, selectedCanvasNodeId }) {
  if (loading) return <Placeholder>Loading output…</Placeholder>;
  const workflowOutput = run?.output ?? run?.result ?? null;
  const selectedNode = selectedCanvasNodeId
    ? (nodes || []).find((n) => {
        const id = n.nodeId || n.canvasNodeId || n.sourceNodeId;
        return id === selectedCanvasNodeId;
      })
    : null;
  const nodeOutput = selectedNode?.output ?? selectedNode?.result ?? null;
  return (
    <div style={{ padding: 0 }}>
      <SectionHeader label="Workflow output" />
      <PayloadBlock
        payload={workflowOutput}
        emptyText="No output yet — run may not have produced a final result."
      />
      {selectedNode && (
        <>
          <SectionHeader
            label={`Selected node · ${selectedNode.label || selectedNode.nodeLabel || selectedCanvasNodeId}`}
          />
          <NodeDebugMeta node={selectedNode} />
          <PayloadBlock
            payload={nodeOutput}
            emptyText="This node hasn't produced output yet."
          />
        </>
      )}
      {!selectedNode && (
        <div style={{
          padding: '10px 12px', fontSize: 10.5, color: '#94a3b8',
          borderTop: '0.5px solid #f1f5f9',
        }}>
          Tip: click a node on the canvas to see its per-task output, retry count, and any error.
        </div>
      )}
    </div>
  );
}

/* ───── Per-node debug meta — Phase 7q-5 ─────────────────────────── *
 * Tight bar above the per-node payload showing the attributes V3
 * operators relied on for inline debug: status pill, duration, retry
 * count, execution mode, and error if any. All optional — the row
 * skips fields the BE didn't surface so deployments with thinner
 * shapes still render cleanly.                                         */
function NodeDebugMeta({ node }) {
  if (!node) return null;
  const status = String(node.status || '').toUpperCase();
  const t = tone(status);
  const dur = fmtDuration(node.startedAt, node.completedAt);
  const retry = node.retryCount ?? node.attempts ?? null;
  const mode = node.executionMode || node.mode || null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      padding: '6px 12px',
      fontSize: 10.5, color: '#475569',
      borderBottom: '0.5px solid #f1f5f9',
      background: '#ffffff',
    }}>
      <span style={{
        padding: '1px 6px', borderRadius: 999,
        fontSize: 9, fontWeight: 700,
        color: t.fg, background: t.bg,
        textTransform: 'uppercase', letterSpacing: 0.3,
      }}>{t.label}</span>
      {dur && <span>· {dur}</span>}
      {retry != null && retry > 0 && (
        <span>· retry {retry}</span>
      )}
      {mode && (
        <span style={{
          padding: '1px 6px', borderRadius: 4,
          background: '#f1f5f9', color: '#475569',
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          fontSize: 10,
        }}>mode: {mode}</span>
      )}
      {node.error && (
        <span style={{ flexBasis: '100%', color: '#991b1b' }}>
          {String(node.error).slice(0, 260)}
        </span>
      )}
    </div>
  );
}

/* ───── Logs tab ──────────────────────────────────────────────────── */
function LogsTab({ nodes, loading }) {
  if (loading) return <Placeholder>Loading logs…</Placeholder>;
  if (!nodes || nodes.length === 0) return <Placeholder>No log entries yet.</Placeholder>;
  // Synthesize a flat event log from per-node start/complete events.
  // The BE may also return a dedicated `logs` array on each node; if so
  // we splice it in. Sort chronologically.
  const events = [];
  for (const n of nodes) {
    const label = n.label || n.nodeLabel || n.nodeId || 'Node';
    if (n.startedAt) {
      events.push({
        ts: n.startedAt, kind: 'start', label,
        message: `${label} started`,
      });
    }
    if (Array.isArray(n.logs)) {
      for (const log of n.logs) {
        events.push({
          ts: log.timestamp || log.ts || n.startedAt,
          kind: log.level || 'info',
          label,
          message: log.message || String(log),
        });
      }
    }
    if (n.completedAt) {
      const status = String(n.status || '').toUpperCase();
      events.push({
        ts: n.completedAt, kind: status === 'FAILED' ? 'error' : 'complete', label,
        message: status === 'FAILED'
          ? `${label} failed${n.error ? `: ${String(n.error).slice(0, 120)}` : ''}`
          : `${label} ${tone(status).label.toLowerCase()}`,
      });
    }
  }
  events.sort((a, b) => new Date(a.ts || 0).getTime() - new Date(b.ts || 0).getTime());
  // Pre-render the log lines as one big string so React doesn't try to
  // reconcile per-line text nodes (cheaper) and so missing-key warnings
  // never fire. The <pre> preserves newlines.
  const body = events.map((ev) => {
    const t = ev.ts ? new Date(ev.ts).toLocaleTimeString() : '·';
    const lvl = (ev.kind || 'info').toUpperCase().padEnd(8, ' ');
    return `${t}  ${lvl}  ${ev.message}`;
  }).join('\n');
  return (
    <pre style={{
      margin: 0,
      padding: 12,
      fontSize: 10.5, lineHeight: 1.5,
      color: '#0f172a',
      background: '#f8fafc',
      fontFamily: 'SFMono-Regular, Menlo, monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}>
      {body}
    </pre>
  );
}

/* ───── Audit tab — Phase 7q-4 ───────────────────────────────────── *
 * Workflow-level audit trail via api.getRunAuditLog. The BE returns a
 * list of entries with shape that varies a bit between deployments;
 * we render defensively — best-effort timestamp, actor, action,
 * message, severity. Falls back to JSON for unknown shapes so nothing
 * is silently dropped.                                                 */
function AuditTab({ entries, loading, error }) {
  if (loading) return <Placeholder>Loading audit log…</Placeholder>;
  if (error)   return <Placeholder>Couldn&apos;t load audit log: {error}</Placeholder>;
  if (!entries || entries.length === 0) {
    return <Placeholder>No audit entries for this run.</Placeholder>;
  }
  // Sort chronologically ascending so the reader follows the run's
  // governance timeline top → bottom.
  const sorted = entries.slice().sort((a, b) => {
    const ta = new Date(a.timestamp || a.ts || a.createdAt || 0).getTime();
    const tb = new Date(b.timestamp || b.ts || b.createdAt || 0).getTime();
    return ta - tb;
  });
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {sorted.map((e, i) => {
        const ts = e.timestamp || e.ts || e.createdAt;
        const sev = String(e.severity || e.level || 'info').toLowerCase();
        const tone = sev === 'error' || sev === 'critical'
          ? { fg: '#991b1b', bg: '#fee2e2' }
          : sev === 'warn' || sev === 'warning'
            ? { fg: '#92400e', bg: '#fef3c7' }
            : { fg: '#475569', bg: '#f1f5f9' };
        return (
          <li key={e.id || `${ts || ''}_${i}`} style={{
            display: 'flex', flexDirection: 'column', gap: 3,
            padding: '8px 12px',
            borderBottom: '0.5px solid #f1f5f9',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{
                padding: '1px 6px', borderRadius: 999,
                fontSize: 9, fontWeight: 700,
                color: tone.fg, background: tone.bg,
                textTransform: 'uppercase', letterSpacing: 0.3,
              }}>{sev}</span>
              {e.action && (
                <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
                  {e.action}
                </span>
              )}
              <span style={{ flex: 1 }} />
              {ts && (
                <span style={{ fontSize: 10, color: '#94a3b8' }}>
                  {new Date(ts).toLocaleTimeString()}
                </span>
              )}
            </div>
            {(e.message || e.detail || e.description) && (
              <div style={{ fontSize: 11, color: '#475569' }}>
                {e.message || e.detail || e.description}
              </div>
            )}
            {(e.actor || e.actorId || e.userId) && (
              <div style={{ fontSize: 10, color: '#94a3b8' }}>
                actor: {e.actor || e.actorId || e.userId}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */
function SectionHeader({ label }) {
  return (
    <div style={{
      padding: '8px 12px 4px',
      fontSize: 9.5, fontWeight: 700, color: '#64748b',
      textTransform: 'uppercase', letterSpacing: 0.4,
      borderBottom: '0.5px solid #f1f5f9',
      background: '#f8fafc',
    }}>{label}</div>
  );
}

function PayloadBlock({ payload, emptyText }) {
  if (payload === null || payload === undefined) {
    return <div style={{ padding: '10px 12px', fontSize: 11, color: '#94a3b8' }}>
      {emptyText || 'No payload.'}
    </div>;
  }
  let body;
  try {
    body = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
  } catch (_) {
    body = String(payload);
  }
  return (
    <pre style={{
      margin: 0,
      padding: 12,
      fontSize: 10.5, lineHeight: 1.5,
      color: '#0f172a',
      background: '#f8fafc',
      fontFamily: 'SFMono-Regular, Menlo, monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}>
      {body}
    </pre>
  );
}

function Placeholder({ children }) {
  return (
    <div style={{
      padding: 24, textAlign: 'center',
      fontSize: 12, color: '#64748b',
    }}>
      {children}
    </div>
  );
}

function tone(status) {
  return STATUS_TONE[status] || { bg: '#f1f5f9', fg: '#475569', label: status || '—' };
}

function fmtDuration(startMs, endMs) {
  if (!startMs) return '';
  const start = typeof startMs === 'number' ? startMs : new Date(startMs).getTime();
  const end = endMs ? (typeof endMs === 'number' ? endMs : new Date(endMs).getTime()) : Date.now();
  if (!Number.isFinite(start)) return '';
  const ms = end - start;
  if (ms < 0) return '';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

const iconBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0,
  background: '#f1f5f9', border: '0.5px solid #e2e8f0',
  borderRadius: 4, color: '#475569', cursor: 'pointer',
  fontFamily: 'inherit',
};
